import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Settings as SettingsIcon, 
  Scale,
  ExternalLink,
  Info,
  Shield,
  Trash2,
  AlertTriangle,
  Loader2,
  Eye,
  EyeOff,
  CreditCard,
  Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showDelete, setShowDelete] = useState(false);
  const [showDeletePassword, setShowDeletePassword] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    setDeleting(true);
    try {
      await axios.post(`${API_URL}/api/auth/delete-my-account`, { password: deletePassword });
      toast.success('Account deleted successfully');
      logout();
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Legal</h1>
        <p className="text-slate-400 mt-1">Important information, disclaimers & tax guidance</p>
      </div>

      {/* About Card */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <SettingsIcon className="h-5 w-5 text-amber-500" />
            About Seafarer Logbook
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-slate-400 space-y-4">
            <p>
              This app helps UK seafarers track their eligibility for the <strong className="text-white">Seafarer's Earnings Deduction (SED)</strong> under 
              HMRC HS205 rules.
            </p>
            <p>
              Beyond eligibility, Seafarer Logbook lets you <strong className="text-white">log every trip</strong> — including multi-leg voyages, holidays, and port calls — and{' '}
              <strong className="text-white">track your earnings</strong> against each voyage. Whether you're on a salaried contract or a day-rate,
              the app calculates your gross earnings per trip and gives you a{' '}
              <strong className="text-white">monthly earnings breakdown</strong> so you always know where you stand financially across each tax year.
            </p>
            <div>
              <p className="font-medium text-slate-300 mb-2">Key HMRC rules tracked:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>365-day eligible period requirement</li>
                <li>Half-day rule — UK days must not exceed half of total days</li>
                <li>183 consecutive UK days rule — breaks the eligible period</li>
                <li>Table restarts — claim recalculates from the breach point</li>
                <li>Foreign port requirement — at least one per employment</li>
                <li>Departure day counts as abroad; return day counts as UK</li>
              </ul>
            </div>
            <p className="text-xs text-slate-500">
              UK tax year runs from 6 April to 5 April. HS205 helpsheet (2025) confirms no rule changes for 2025/26.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Important Tax Notes */}
      <Card className="bg-slate-800 border-slate-700 border-l-4 border-l-blue-500">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Info className="h-5 w-5 text-blue-400" />
            Important Tax Notes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-slate-400 space-y-5">
            <p className="text-slate-300">
              The following tax matters are <strong className="text-white">not calculated by this app</strong> but may affect your overall tax position. 
              Please discuss these with your accountant or tax adviser.
            </p>

            <div className="space-y-4">
              <div className="p-3 bg-slate-700/40 rounded-lg">
                <p className="font-medium text-slate-300 mb-1">1. National Insurance Contributions (NICs)</p>
                <p>
                  SED provides relief from <strong className="text-white">income tax only</strong>. You will still pay National Insurance (Class 1 for employees) 
                  on all seafaring earnings. There is no NIC exemption linked to SED.
                </p>
              </div>

              <div className="p-3 bg-slate-700/40 rounded-lg">
                <p className="font-medium text-slate-300 mb-1">2. Pension Contributions</p>
                <p>
                  Your seafaring earnings still count as "relevant UK earnings" for pension contribution purposes, even with SED. 
                  Pension providers can claim <strong className="text-white">basic rate tax relief (20%)</strong> from HMRC on your contributions, 
                  but no higher-rate relief is available since no higher-rate tax is paid on SED-exempt earnings. 
                  The annual allowance for 2025/26 is £60,000.
                </p>
              </div>

              <div className="p-3 bg-slate-700/40 rounded-lg">
                <p className="font-medium text-slate-300 mb-1">3. Student Loan Repayments</p>
                <p>
                  If you have UK student loans and work overseas, the <strong className="text-white">Student Loans Company (SLC)</strong> takes over 
                  collection from HMRC using different overseas thresholds. This can cause double-charging if not managed. 
                  Contact the SLC directly to arrange an informal "stand over" and avoid overpayments.
                </p>
              </div>

              <div className="p-3 bg-slate-700/40 rounded-lg">
                <p className="font-medium text-slate-300 mb-1">4. Foreign Tax Credit Relief</p>
                <p>
                  If you pay tax in a foreign jurisdiction on the same income, you may be entitled to 
                  <strong className="text-white">Foreign Tax Credit Relief</strong> (see HMRC HS263). The credit is capped at the UK tax 
                  attributable to that income. Excess foreign tax cannot generate a UK repayment.
                </p>
              </div>

              <div className="p-3 bg-slate-700/40 rounded-lg">
                <p className="font-medium text-slate-300 mb-1">5. Making Tax Digital (MTD)</p>
                <p>
                  From <strong className="text-white">April 2026</strong>, self-employed individuals or those with property income over 
                  £50,000 must report quarterly via MTD-compatible software (dropping to £30,000 from April 2027). 
                  If you file Self Assessment for your SED claim, check whether MTD applies to you.
                </p>
              </div>

              <div className="p-3 bg-slate-700/40 rounded-lg">
                <p className="font-medium text-slate-300 mb-1">6. Offshore Installations Exclusion</p>
                <p>
                  SED does <strong className="text-white">not</strong> apply to work on offshore oil/gas installations, including fixed or floating 
                  platforms, FPSOs, drilling units, flotels, or wind farm installations. Only work on 
                  <strong className="text-white">qualifying ships</strong> (navigable vessels engaged on voyages beginning or ending outside the UK) is eligible.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 mt-2">
              <p className="text-xs text-slate-500">Sources: HMRC HS205 (2025), GOV.UK Seafarers Earnings Deduction guidance, HMRC HS263 Foreign Tax Credit Relief</p>
              <div className="flex gap-4">
                <a 
                  href="https://www.gov.uk/government/publications/seafarers-earnings-deduction-hs205-self-assessment-helpsheet/hs205-seafarers-earnings-deduction-2025" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 underline text-xs"
                >
                  HS205 Helpsheet (2025) <ExternalLink className="h-3 w-3" />
                </a>
                <a 
                  href="https://www.gov.uk/guidance/seafarers-earnings-deduction-tax-relief-if-you-work-on-a-ship" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 underline text-xs"
                >
                  HMRC SED Guidance <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Subscription & Referral Rewards Terms */}
      <Card className="bg-slate-800 border-slate-700 border-l-4 border-l-cyan-500">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Scale className="h-5 w-5 text-cyan-500" />
            Subscription & Referral Rewards
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-slate-300 space-y-4">
            <div className="space-y-3 text-slate-400">
              <p>
                <strong className="text-slate-300">1. Subscription:</strong> Seafarer Logbook is a paid service available at 
                <strong className="text-white"> £3.99 per month</strong> or <strong className="text-white">£45 per year</strong>. Subscriptions auto-renew until cancelled.
              </p>

              <p>
                <strong className="text-slate-300">2. Referral Programme:</strong> Registered users may invite others to join Seafarer Logbook. Each user may send up to 
                <strong className="text-white"> 12 successful invitations per rolling 12-month period</strong>. Only accepted invitations count towards this limit.
              </p>

              <p>
                <strong className="text-slate-300">3. Invitation Expiry:</strong> All invitations expire 
                <strong className="text-white"> 24 hours</strong> after being sent. If the invitee does not create an account within 24 hours, the invitation is automatically cancelled. Expired invitations do not count against the sender's annual limit and may be re-sent.
              </p>

              <p>
                <strong className="text-slate-300">4. Referral Rewards:</strong> When an invitation is accepted within 24 hours, 
                <strong className="text-white"> both the inviter and the invitee receive 7 days (1 week) of free subscription</strong>. Rewards for the inviter are accumulated and applied to the end of their billing year. For example, a user with 12 successful referrals in a year would receive 12 weeks (approximately 3 months) of free subscription at the end of their billing year.
              </p>

              <p>
                <strong className="text-slate-300">5. Reward Application:</strong> Free weeks earned through referrals are applied at the end of the user's current billing year. During the free period, the user retains full access to all features. Once the free period ends, normal monthly billing resumes.
              </p>

              <p>
                <strong className="text-slate-300">6. Modifications:</strong> Seafarer Logbook reserves the right to modify, suspend, or terminate the referral programme, reward amounts, invitation limits, or subscription pricing at any time. Users will be notified of material changes via email.
              </p>

              <p>
                <strong className="text-slate-300">7. Abuse Prevention:</strong> Referral rewards are intended for genuine invitations to real individuals. Any attempt to abuse the referral system (e.g. creating fake accounts, self-referrals) may result in the forfeiture of rewards and/or account suspension.
              </p>
            </div>
            
            <p className="text-xs text-slate-500 mt-4">
              Last updated: March 2026.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Legal Disclaimer */}
      <Card className="bg-slate-800 border-slate-700 border-l-4 border-l-amber-500">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Scale className="h-5 w-5 text-amber-500" />
            Legal Disclaimer
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-slate-300 space-y-4">
            <p className="font-medium text-amber-400">
              PLEASE READ CAREFULLY BEFORE USING THIS APPLICATION
            </p>
            
            <div className="space-y-3 text-slate-400">
              <p>
                <strong className="text-slate-300">1. Guidance Only:</strong> This application is provided for 
                <strong className="text-white"> guidance and informational purposes only</strong>. It is not 
                intended to constitute professional tax advice, legal advice, or any other form of professional 
                advice. The calculations and information provided should not be relied upon as a substitute 
                for consultation with a qualified tax professional or accountant.
              </p>
              
              <p>
                <strong className="text-slate-300">2. No Warranty:</strong> While every effort has been made to 
                ensure that the calculations in this application comply with current UK HMRC rules for 
                Seafarer's Earnings Deduction (SED) under HS205, <strong className="text-white">no warranty 
                or guarantee is given</strong> as to the accuracy, completeness, or reliability of the 
                calculations or any other information provided by this application.
              </p>
              
              <p>
                <strong className="text-slate-300">3. Limitation of Liability:</strong> To the fullest extent 
                permitted by applicable law, the developer(s) and operator(s) of this application 
                <strong className="text-white"> shall not be liable</strong> for any direct, indirect, 
                incidental, special, consequential, or punitive damages, including but not limited to:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Incorrect tax calculations or assessments</li>
                <li>Penalties, fines, or interest imposed by HMRC</li>
                <li>Loss of income, profits, or tax relief</li>
                <li>Any other financial loss or damage</li>
              </ul>
              <p className="text-slate-400">
                arising from or in connection with the use of, or reliance upon, this application or its calculations.
              </p>
              
              <p>
                <strong className="text-slate-300">4. Changes in Tax Law:</strong> UK tax legislation is 
                subject to change. HMRC may update their rules, guidance, or interpretation at any time. 
                This application may not immediately reflect such changes. Users are advised to verify 
                current requirements directly with HMRC or a qualified tax professional.
              </p>
              
              <p>
                <strong className="text-slate-300">5. User Responsibility:</strong> It is the sole 
                responsibility of the user to:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Verify all calculations independently before submitting any tax returns</li>
                <li>Maintain accurate records as required by HMRC</li>
                <li>Seek professional tax advice where appropriate</li>
                <li>Ensure compliance with all applicable tax laws and regulations</li>
              </ul>
              
              <p>
                <strong className="text-slate-300">6. Official HMRC Guidance:</strong> For authoritative 
                information on Seafarer's Earnings Deduction, please refer to:
              </p>
              <a 
                href="https://www.gov.uk/guidance/seafarers-earnings-deduction-tax-relief-if-you-work-on-a-ship" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-amber-400 hover:text-amber-300 underline"
              >
                HMRC: Seafarers Earnings Deduction
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
            
            <div className="mt-6 p-4 bg-amber-900/20 border border-amber-600/30 rounded-lg">
              <p className="text-amber-300 text-xs leading-relaxed">
                <strong>BY USING THIS APPLICATION, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, 
                AND AGREE TO THIS DISCLAIMER.</strong> If you do not agree with these terms, you should 
                not use this application for any purpose.
              </p>
            </div>
            
            <p className="text-xs text-slate-500 mt-4">
              This disclaimer is governed by and construed in accordance with the laws of England and Wales. 
              Last updated: March 2026.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* HMRC Non-Affiliation Disclaimer */}
      <Card className="bg-slate-800 border-slate-700 border-l-4 border-l-red-500" data-testid="hmrc-disclaimer-card">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            HMRC Non-Affiliation Notice
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-slate-300 space-y-4">
            <div className="p-4 bg-red-950/20 border border-red-500/30 rounded-lg">
              <p className="text-red-300 text-sm leading-relaxed">
                <strong>This application is not affiliated with, endorsed by, approved by, or in any
                way officially connected to HM Revenue & Customs (HMRC) or any department of the
                UK Government.</strong>
              </p>
            </div>

            <div className="space-y-3 text-slate-400">
              <p>
                Seafarer Logbook is an <strong className="text-white">independent, privately developed tool</strong> designed
                to assist UK seafarers in tracking their voyages and estimating eligibility for the Seafarer's
                Earnings Deduction (SED) under HMRC helpsheet HS205. It has no official status and does not
                represent HMRC guidance.
              </p>

              <p>
                The name "HMRC", the HMRC logo, and all related marks are <strong className="text-white">trademarks
                or registered trademarks of HM Revenue & Customs</strong>. Any references to HMRC
                within this application are made solely for informational purposes to direct users to the
                relevant official guidance.
              </p>

              <p>
                All tax calculations, estimates, and eligibility assessments provided by this application are
                produced by the app's own logic and <strong className="text-white">have not been verified, validated,
                or approved by HMRC</strong>. Users should always confirm their tax position with HMRC directly
                or seek independent professional advice.
              </p>
            </div>

            <div className="flex gap-4 mt-2">
              <a
                href="https://www.gov.uk/government/organisations/hm-revenue-customs"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-red-400 hover:text-red-300 underline text-xs"
              >
                Official HMRC Website <ExternalLink className="h-3 w-3" />
              </a>
              <a
                href="https://www.gov.uk/government/publications/seafarers-earnings-deduction-hs205-self-assessment-helpsheet/hs205-seafarers-earnings-deduction-2025"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-red-400 hover:text-red-300 underline text-xs"
              >
                Official HS205 Helpsheet <ExternalLink className="h-3 w-3" />
              </a>
            </div>

            <p className="text-xs text-slate-500 mt-2">
              Last updated: March 2026.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Terms of Service */}
      <Card className="bg-slate-800 border-slate-700 border-l-4 border-l-purple-500" data-testid="terms-of-service-card">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Scale className="h-5 w-5 text-purple-400" />
            Terms of Service
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-slate-300 space-y-3">
            <div className="space-y-3 text-slate-400">
              <p>
                <strong className="text-slate-300">1. Acceptance:</strong> By accessing or using Seafarer Logbook ("the App"), you agree to be bound by these Terms of Service. If you do not agree, you must not use the App.
              </p>
              <p>
                <strong className="text-slate-300">2. Licence to Use:</strong> We grant you a limited, non-exclusive, non-transferable, revocable licence to use the App for your personal, non-commercial tax-tracking purposes. This licence does not include any right to copy, modify, distribute, sell, lease, reverse-engineer, decompile, or create derivative works of any part of the App.
              </p>
              <p>
                <strong className="text-slate-300">3. Intellectual Property:</strong> All content, features, functionality, source code, design, graphics, logos, and trademarks within the App are the exclusive property of D Grassie and are protected by UK and international copyright, trademark, and intellectual property laws. Unauthorised reproduction or distribution is strictly prohibited.
              </p>
              <p>
                <strong className="text-slate-300">4. User Content & Data:</strong> You retain ownership of any data you input into the App (trip records, earnings, documents). By using the App, you grant us a limited licence to store and process your data solely for the purpose of providing the service. We will not sell or share your personal data with third parties except as required by law.
              </p>
              <p>
                <strong className="text-slate-300">5. Prohibited Conduct:</strong> You agree not to:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Copy, reproduce, or scrape any part of the App or its content</li>
                <li>Reverse-engineer, decompile, or disassemble the App's source code</li>
                <li>Use automated tools (bots, scrapers) to access the App</li>
                <li>Attempt to gain unauthorised access to other user accounts or data</li>
                <li>Use the App for any unlawful purpose or to facilitate tax evasion</li>
                <li>Redistribute, sublicence, or resell access to the App</li>
              </ul>
              <p>
                <strong className="text-slate-300">6. Account Termination:</strong> We reserve the right to suspend or terminate your account at any time, without notice, for conduct that we believe violates these Terms, is harmful to other users, or is otherwise objectionable. You may delete your account at any time via the settings below.
              </p>
              <p>
                <strong className="text-slate-300">7. Limitation of Liability:</strong> The App is provided "as is" without warranties of any kind. To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the App, including but not limited to incorrect tax calculations, HMRC penalties, or financial losses.
              </p>
              <p>
                <strong className="text-slate-300">8. Indemnification:</strong> You agree to indemnify and hold harmless D Grassie and any affiliated parties from any claims, damages, or expenses arising from your use of the App or your violation of these Terms.
              </p>
              <p>
                <strong className="text-slate-300">9. Governing Law:</strong> These Terms are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.
              </p>
              <p>
                <strong className="text-slate-300">10. Changes to Terms:</strong> We may update these Terms at any time. Continued use of the App after changes constitutes acceptance of the revised Terms. Material changes will be notified via email or in-app notice.
              </p>
            </div>
            <p className="text-xs text-slate-500 mt-4">
              Last updated: April 2026.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Cancel Subscription */}
      <Card className="bg-slate-800 border-slate-700 border-l-4 border-l-sky-500">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-sky-400" />
            Subscription Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-slate-400 space-y-3">
            <p>
              Your subscription auto-renews until cancelled. To manage or cancel your subscription,
              please contact support or use the cancel option below.
            </p>
            <Button variant="outline" size="sm"
              onClick={() => navigate('/subscription')}
              className="border-slate-600 text-slate-400 hover:text-white"
              data-testid="manage-subscription-btn">
              <CreditCard className="h-3.5 w-3.5 mr-1.5" />
              View / Cancel Subscription
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Privacy & Data */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Shield className="h-5 w-5 text-amber-500" />
            Privacy & Data
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-300 font-medium">Privacy Policy</p>
              <p className="text-xs text-slate-500">How we collect, use, and protect your data</p>
            </div>
            <Link to="/privacy">
              <Button variant="outline" size="sm" className="border-slate-600 text-slate-400 hover:text-white" data-testid="privacy-policy-link">
                <Shield className="h-3.5 w-3.5 mr-1.5" />
                View
              </Button>
            </Link>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-300 font-medium">Terms of Service</p>
              <p className="text-xs text-slate-500">Your contract with Seafarer Logbook</p>
            </div>
            <Link to="/terms">
              <Button variant="outline" size="sm" className="border-slate-600 text-slate-400 hover:text-white" data-testid="terms-link">
                <Shield className="h-3.5 w-3.5 mr-1.5" />
                View
              </Button>
            </Link>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-300 font-medium">Refund Policy</p>
              <p className="text-xs text-slate-500">When and how refunds apply</p>
            </div>
            <Link to="/refund">
              <Button variant="outline" size="sm" className="border-slate-600 text-slate-400 hover:text-white" data-testid="refund-link">
                <Shield className="h-3.5 w-3.5 mr-1.5" />
                View
              </Button>
            </Link>
          </div>

          {/* GDPR data export — Article 15 (access) + Article 20 (portability) */}
          <div className="flex items-center justify-between pt-3 border-t border-slate-700">
            <div>
              <p className="text-sm text-slate-300 font-medium">Export My Data</p>
              <p className="text-xs text-slate-500">Download a copy of all data we hold about you (UK GDPR)</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const res = await axios.get(`${API_URL}/api/auth/export-my-data`, { responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
                  const a = document.createElement('a');
                  a.href = url;
                  const cd = res.headers['content-disposition'] || '';
                  const m = cd.match(/filename="([^"]+)"/);
                  a.download = m ? m[1] : 'seafarer-logbook-data-export.json';
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success('Data export downloaded');
                } catch {
                  toast.error('Failed to export data');
                }
              }}
              className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
              data-testid="export-my-data-btn"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              Export
            </Button>
          </div>

          {/* Self-service account deletion */}
          <div className="pt-3 border-t border-slate-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-400 font-medium">Delete Account</p>
                  <p className="text-xs text-slate-500">Permanently delete your account and all associated data</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDelete(!showDelete)}
                  className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                  data-testid="delete-account-toggle-btn"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  {showDelete ? 'Cancel' : 'Delete'}
                </Button>
              </div>

              {showDelete && (
                <div className="mt-4 p-4 bg-red-950/30 border border-red-500/30 rounded-lg space-y-3" data-testid="delete-account-form">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-red-300 leading-relaxed">
                      This action is <strong>permanent and irreversible</strong>. All your trips, documents, earnings, expenses, and account data will be deleted immediately. Payment transaction records may be retained for up to 7 years for legal compliance.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className="relative">
                      <Input
                        type={showDeletePassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        className="bg-slate-900 border-slate-600 text-white h-9 text-sm pr-10"
                        data-testid="delete-account-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowDeletePassword(!showDeletePassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                        tabIndex={-1}
                      >
                        {showDeletePassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                    <Input
                      type="text"
                      placeholder="Type DELETE to confirm"
                      value={deleteConfirm}
                      onChange={(e) => setDeleteConfirm(e.target.value)}
                      className="bg-slate-900 border-slate-600 text-white h-9 text-sm"
                      data-testid="delete-account-confirm"
                    />
                    <Button
                      onClick={handleDeleteAccount}
                      disabled={deleting || deleteConfirm !== 'DELETE' || !deletePassword}
                      className="w-full bg-red-600 hover:bg-red-700 text-white h-9 text-sm"
                      data-testid="delete-account-submit-btn"
                    >
                      {deleting ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</>
                      ) : (
                        <><Trash2 className="h-4 w-4 mr-2" />Permanently Delete My Account</>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
